import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, EllipsisVertical, Pencil, RotateCw, Trash2 } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import api from '../../services/api';
import { API_BASE_URL } from '../../services/apiConfig';
import { TimeOffCategory, TimeOffType, TimeOffStatus } from '../../types/timeOff';
import { sbg } from '../../theme/sbgTokens';
import {
  ConfirmDialog,
  CountBadge,
  DefinitionList,
  ErrorState,
  FilterChip,
  PersonCell,
  SectionCard,
  Sheet,
  StatusPill,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../ui';
import type { StatusTone } from '../ui';
import { formatCount, formatDateRange, formatDateTime, formatNumber, formatTime, MISSING } from '../../utils/format';
import {
  CATEGORY_ORDER,
  CATEGORY_OPTION_LABEL,
  categoryChipStyle,
  TIME_OFF_TYPE_ORDER,
  TIME_OFF_TYPE_OPTION_LABEL,
  timeOffTypeChipStyle,
  timeOffTypeLabel,
} from './categoryChipStyle';
import { SeriesLabel } from './calendar-parts/SeriesChip';
import { describeRequestFailure, failureAdvice, formatHours, RequestFailure } from './calendar-parts/calendarFormat';
import ErrorDetails from './calendar-parts/ErrorDetails';

// Category and type colors live in ./categoryChipStyle.ts (shared with
// OOORequestsList and the calendar). In this table they are plain text with a
// series dot; status colors are only for true states (needs a category, AI
// failures), so a row never carries more than one or two badges.

interface TimeOffEntry {
  _id: string;
  employee: {
    _id: string;
    name: string;
    email: string;
    department: {
      _id: string;
      name: string;
    };
  };
  startDate: string;
  endDate: string;
  type: TimeOffType;
  hours: number;
  status: TimeOffStatus;
  notes: string;
  category?: TimeOffCategory;
  reason?: string;
  originalCalendarSubject?: string;
  reconciled: boolean;
  needsReview?: boolean;
  reviewReason?: string | null;
  isRecurring?: boolean;
}

interface ErrorInfo {
  /** What happened: "Couldn't save Avery Adams's event". */
  title: string;
  /** Why, when the response says; the technical detail sits behind "Details". */
  failure: RequestFailure | null;
}

const COLUMNS = 6;

/** Fixed column plan: the three text columns share what's left, so nothing hides off the right edge. */
const COLUMN_WIDTHS = { when: 172, type: 112, actions: 212 } as const;

/**
 * Date inputs need `yyyy-MM-dd`. Entry dates are calendar dates already, so
 * take them as written; only timestamps go through `Date` (which would shift a
 * bare date back a day west of UTC).
 */
const toDateInputValue = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd');
};

const flagTone = (reviewReason?: string | null): StatusTone => {
  switch (reviewReason) {
    case 'uncategorized':
      return 'warning';
    case 'openai_error':
    case 'reanalyze_failed':
      return 'danger';
    default:
      return 'neutral';
  }
};

/** The actions column stays pinned to the right edge (sm+) so the row's only path to an action never scrolls away. */
const stickyEnd = (header = false) =>
  ({
    position: { xs: 'static', sm: 'sticky' },
    right: 0,
    zIndex: 1,
    bgcolor: header ? sbg.bg.surfaceSubtle : sbg.bg.surface,
    borderLeft: { sm: `1px solid ${sbg.border.subtle}` },
    ...(header ? null : { '.MuiTableBody-root .MuiTableRow-root:hover > &': { bgcolor: sbg.bg.hover } }),
  }) as const;

const OOOReconciler: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOffEntry[]>([]);
  const [editingEntry, setEditingEntry] = useState<TimeOffEntry | null>(null);
  const [sheetError, setSheetError] = useState<ErrorInfo | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [showNeedsReviewOnly, setShowNeedsReviewOnly] = useState<boolean>(true);
  const [showRecurringOnly, setShowRecurringOnly] = useState<boolean>(false);
  // Presentational: the row "More actions" menu and the two confirmations. The
  // dialogs keep their target after closing so their copy holds through the exit.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; entry: TimeOffEntry } | null>(null);
  const [discardDialog, setDiscardDialog] = useState<{ open: boolean; entry: TimeOffEntry | null }>({ open: false, entry: null });
  const [bulkDialog, setBulkDialog] = useState<{ open: boolean; entries: TimeOffEntry[]; hidden: number }>({
    open: false,
    entries: [],
    hidden: 0,
  });

  const needsReview = (entry: TimeOffEntry) => entry.needsReview || !entry.category || !entry.reason;
  const recurringEntries = timeOffEntries.filter((entry) => entry.isRecurring);
  const needsReviewCount = timeOffEntries.filter(needsReview).length;

  const formatReviewFlag = (reviewReason?: string | null) => {
    switch (reviewReason) {
      case 'recurring_calendar_series':
        return 'Recurring series review';
      case 'uncategorized':
        return 'Needs a category';
      case 'openai_error':
        return 'AI review failed';
      case 'reanalyze_failed':
        return 'AI retry failed';
      default: {
        const text = reviewReason ? reviewReason.replace(/_/g, ' ') : '';
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
      }
    }
  };

  const visibleEntries = timeOffEntries.filter((entry) => {
    if (showNeedsReviewOnly && !needsReview(entry)) return false;
    if (showRecurringOnly && !entry.isRecurring) return false;
    return true;
  });

  const fetchUnreconciledEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${API_BASE_URL}/time-off/unreconciled/list`);
      setTimeOffEntries(response.data.data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError({ title: 'Couldn’t load Outlook events', failure: describeRequestFailure(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreconciledEntries();
  }, []);

  /** Saves one event. Returns the failure instead of showing it, so the caller decides where it belongs. */
  const reconcileEntry = async (entry: TimeOffEntry, changes: Partial<TimeOffEntry> = {}): Promise<ErrorInfo | null> => {
    setLoading(true);
    setError(null);
    try {
      // Let backend decide status based on type unless explicitly set
      const updatedEntry = {
        ...changes,
        reconciled: true
      };

      await api.put(`${API_BASE_URL}/time-off/${entry._id}/reconcile`, updatedEntry);

      // Update the local state
      setTimeOffEntries(prev => prev.filter(e => e._id !== entry._id));
      const name = entry.employee.name;
      setSuccessMessage(
        changes.status === TimeOffStatus.REJECTED
          ? `Discarded ${name}’s event. It won’t count as time off.`
          : changes.type
            ? `Marked ${name}’s event reviewed as ${timeOffTypeLabel(changes.type)}.`
            : `Marked ${name}’s event reviewed.`
      );
      return null;
    } catch (err) {
      return { title: `Couldn’t save ${entry.employee.name}’s event`, failure: describeRequestFailure(err) };
    } finally {
      setLoading(false);
    }
  };

  /** Row actions: a failure shows in the page alert above the table. */
  const handleReconcile = async (entry: TimeOffEntry, changes: Partial<TimeOffEntry> = {}) => {
    const failed = await reconcileEntry(entry, changes);
    if (failed) setError(failed);
    return !failed;
  };

  const handleBulkReconcile = async (targets: TimeOffEntry[] = visibleEntries) => {
    if (targets.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const entries = targets.map(entry => ({
        id: entry._id,
        reconciled: true,
      }));

      await api.put(`${API_BASE_URL}/time-off/bulk/reconcile`, { entries });

      setTimeOffEntries(prev => prev.filter(entry => !targets.some(target => target._id === entry._id)));
      setSuccessMessage(`Marked ${formatCount(entries.length, 'event')} reviewed.`);
    } catch (err) {
      setError({ title: 'Couldn’t mark the events reviewed', failure: describeRequestFailure(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async (entryId: string) => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`${API_BASE_URL}/time-off/${entryId}/reanalyze`);
      await fetchUnreconciledEntries();
      setSuccessMessage('Re-ran AI review for 1 event.');
    } catch (err) {
      setError({ title: 'Couldn’t re-run AI review', failure: describeRequestFailure(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReanalyze = async () => {
    if (visibleEntries.length === 0) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await api.post(`${API_BASE_URL}/time-off/reanalyze`, {
        entryIds: visibleEntries.map((entry) => entry._id),
      });
      await fetchUnreconciledEntries();
      const result = response.data?.data as {
        updatedCount?: number;
        failedCount?: number;
        failures?: Array<{ id: string; error: string }>;
      } | undefined;
      const updatedCount = result?.updatedCount ?? 0;
      const failedCount = result?.failedCount ?? 0;
      if (failedCount > 0) {
        if (updatedCount > 0) {
          setSuccessMessage(`Re-ran AI review for ${formatCount(updatedCount, 'event')}.`);
        }
        const firstFailure = result?.failures?.[0]?.error;
        setError({
          title: `${formatCount(failedCount, 'event')} ${failedCount === 1 ? 'wasn’t' : 'weren’t'} re-analyzed`,
          failure: firstFailure ? { status: null, detail: firstFailure } : null,
        });
      } else {
        setSuccessMessage(`Re-ran AI review for ${formatCount(updatedCount, 'event')}.`);
      }
    } catch (err) {
      setError({ title: 'Couldn’t re-run AI review', failure: describeRequestFailure(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = async (entry: TimeOffEntry) => {
    await handleReconcile(entry, { status: TimeOffStatus.REJECTED, notes: `${entry.notes || ''} | Discarded by HR review`.trim() });
  };

  const handleEdit = (entry: TimeOffEntry) => {
    setSheetError(null);
    setEditingEntry({...entry});
  };

  // Close the sheet only when the save worked; on failure keep every edit and say so inside the sheet.
  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    setSheetError(null);
    const failed = await reconcileEntry(editingEntry, editingEntry);
    if (failed) {
      setSheetError(failed);
      return;
    }
    setEditingEntry(null);
  };

  const handleCloseDialog = () => {
    setEditingEntry(null);
  };

  const getDurationDays = (startDate: string, endDate: string) =>
    differenceInDays(new Date(endDate), new Date(startDate)) + 1;

  const clearFilters = () => {
    setShowNeedsReviewOnly(false);
    setShowRecurringOnly(false);
  };

  const closeRowMenu = () => setRowMenu(null);
  const closeDiscardDialog = () => setDiscardDialog((current) => ({ ...current, open: false }));
  const closeBulkDialog = () => setBulkDialog((current) => ({ ...current, open: false }));

  /* ---------------------------------------------------------------------- */
  /* Table body                                                              */
  /* ---------------------------------------------------------------------- */

  const initialLoading = loading && !lastRefreshed;
  const initialError = Boolean(error) && !lastRefreshed;
  const visibleCount = visibleEntries.length;
  const hiddenCount = timeOffEntries.length - visibleCount;
  const activeFilterNames = [showNeedsReviewOnly ? 'Needs review' : null, showRecurringOnly ? 'Recurring' : null].filter(Boolean);

  const renderBody = () => {
    if (initialLoading) return <TableSkeletonRows columns={COLUMNS} rows={6} />;
    if (initialError && error) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={COLUMNS} sx={{ borderBottom: 0 }}>
            <Box sx={{ display: 'grid', justifyItems: 'center', pb: error.failure?.detail ? 3 : 0, color: sbg.text.secondary }}>
              <ErrorState
                compact
                title={error.title}
                body={failureAdvice(error.failure, 'the review queue')}
                onRetry={fetchUnreconciledEntries}
              />
              <ErrorDetails detail={error.failure?.detail} />
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    if (timeOffEntries.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="Every Outlook event is reconciled"
          body="New out-of-office events from Outlook land here for review after each calendar sync."
        />
      );
    }
    if (visibleCount === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMNS}
          title="No events match these filters"
          body={`${formatCount(hiddenCount, 'event')} ${hiddenCount === 1 ? 'is' : 'are'} hidden by the ${activeFilterNames.join(' and ')} ${activeFilterNames.length === 1 ? 'filter' : 'filters'}.`}
          action={
            <Button variant="text" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      );
    }

    return visibleEntries.map((entry) => {
      const name = entry.employee.name;
      const days = getDurationDays(entry.startDate, entry.endDate);
      const typeLabel = timeOffTypeLabel(entry.type);
      const reviewFlag =
        entry.reviewReason && entry.reviewReason !== 'recurring_calendar_series' ? formatReviewFlag(entry.reviewReason) : null;
      // Being recurring is a property, not a problem: a neutral pill, dropped while the Recurring filter already says so.
      const showRecurringPill = Boolean(entry.isRecurring) && !showRecurringOnly;
      const contact = [entry.employee.department?.name, entry.employee.email].filter(Boolean).join(' · ');
      return (
        <TableRow key={entry._id}>
          <TableCell>
            <Box title={contact || undefined} sx={{ minWidth: 0 }}>
              <PersonCell name={name} secondary={contact || MISSING} />
            </Box>
          </TableCell>
          <TableCell>
            <Typography
              variant="body1"
              component="div"
              noWrap
              title={entry.originalCalendarSubject || undefined}
              sx={{ color: entry.originalCalendarSubject ? sbg.text.primary : sbg.text.tertiary }}
            >
              {entry.originalCalendarSubject || MISSING}
            </Typography>
            {(reviewFlag || showRecurringPill) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {reviewFlag && <StatusPill tone={flagTone(entry.reviewReason)}>{reviewFlag}</StatusPill>}
                {showRecurringPill && <StatusPill tone="neutral">Recurring series</StatusPill>}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body1" component="div" noWrap sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
              {formatDateRange(entry.startDate, entry.endDate)}
            </Typography>
            <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
              <span>{formatCount(days, 'day')}</span>
              {' · '}
              <span>{formatHours(entry.hours)}</span>
            </Typography>
          </TableCell>
          <TableCell>
            <SeriesLabel tone={categoryChipStyle(entry.category)} muted={!entry.category} />
            {entry.reason && (
              <Typography
                variant="body2"
                component="div"
                title={entry.reason}
                sx={{
                  mt: 0.25,
                  color: sbg.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {entry.reason}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <SeriesLabel tone={timeOffTypeChipStyle(entry.type)} />
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap', ...stickyEnd() }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => handleReconcile(entry, { type: entry.type })}
                disabled={loading}
                aria-label={`Confirm ${typeLabel} for ${name}`}
                sx={{ minWidth: 112 }}
              >
                Confirm {typeLabel}
              </Button>
              <Tooltip title="Edit details">
                <span>
                  <IconButton size="small" aria-label={`Edit ${name}’s event`} onClick={() => handleEdit(entry)} disabled={loading}>
                    <Pencil />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="More actions">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`More actions for ${name}`}
                    aria-haspopup="menu"
                    aria-expanded={rowMenu?.entry._id === entry._id || undefined}
                    onClick={(event) => setRowMenu({ anchor: event.currentTarget, entry })}
                    disabled={loading}
                  >
                    <EllipsisVertical />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  };

  const bulkDisabledReason = loading ? 'Wait for the current update to finish' : null;
  const showTableActions = !initialLoading && !initialError && timeOffEntries.length > 0;
  const showRecurringNote = showTableActions && recurringEntries.length > 0 && !showRecurringOnly;
  const showPageError = Boolean(error) && !initialError;

  const refreshButton = (
    <Tooltip title="Refresh Outlook events">
      <span>
        <IconButton aria-label="Refresh Outlook events" onClick={fetchUnreconciledEntries} disabled={loading} aria-busy={loading || undefined}>
          <RotateCw />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      <SectionCard
        flush
        aria-label="Reconcile Outlook events"
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            Reconcile Outlook events
            {lastRefreshed && timeOffEntries.length > 0 && <CountBadge count={timeOffEntries.length} />}
          </Box>
        }
        description={
          <>
            Confirm the type and category of out-of-office events imported from Outlook.
            {lastRefreshed && (
              <Box component="span" sx={{ display: 'block', mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                <Tooltip title={formatDateTime(lastRefreshed)}>
                  <span>Updated {formatTime(lastRefreshed)}</span>
                </Tooltip>
              </Box>
            )}
          </>
        }
        action={refreshButton}
      >
        {(showPageError || showRecurringNote) && (
          <Box sx={{ display: 'grid', gap: 1.5, p: 2, borderBottom: `1px solid ${sbg.border.subtle}` }}>
            {showPageError && error && (
              <Alert severity="error" onClose={() => setError(null)}>
                <AlertTitle>{error.title}</AlertTitle>
                {failureAdvice(error.failure, 'this queue')} The list below shows the last saved state.
                <ErrorDetails detail={error.failure?.detail} />
              </Alert>
            )}
            {showRecurringNote && (
              <Alert severity="info">
                <AlertTitle>{formatCount(recurringEntries.length, 'recurring event')} to confirm</AlertTitle>
                Recurring Outlook imports are listed so HR can confirm the whole series came through. The Recurring filter shows
                them together.
              </Alert>
            )}
          </Box>
        )}

        {showTableActions && (
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${sbg.border.subtle}` }}>
            <Toolbar
              end={
                visibleCount > 0 ? (
                  <>
                    <Tooltip title={bulkDisabledReason ?? ''}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<RotateCw size={16} />}
                          onClick={handleBulkReanalyze}
                          disabled={Boolean(bulkDisabledReason)}
                        >
                          Re-run AI on {formatCount(visibleCount, 'event')}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={bulkDisabledReason ?? ''}>
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Check size={16} />}
                          onClick={() => setBulkDialog({ open: true, entries: visibleEntries, hidden: hiddenCount })}
                          disabled={Boolean(bulkDisabledReason)}
                        >
                          Mark {formatCount(visibleCount, 'event')} reviewed
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                ) : undefined
              }
            >
              <FilterChip
                selected={showNeedsReviewOnly}
                onClick={() => setShowNeedsReviewOnly((on) => !on)}
                count={needsReviewCount}
              >
                Needs review
              </FilterChip>
              <FilterChip
                selected={showRecurringOnly}
                onClick={() => setShowRecurringOnly((on) => !on)}
                count={recurringEntries.length}
              >
                Recurring
              </FilterChip>
              {hiddenCount > 0 && visibleCount > 0 && (
                <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, ml: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(hiddenCount)} hidden by filters
                </Typography>
              )}
            </Toolbar>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
          {loading && lastRefreshed && (
            <LinearProgress
              aria-label="Updating events"
              sx={{ position: 'absolute', top: 36, left: 0, right: 0, height: 2, zIndex: 2 }}
            />
          )}
          <Box
            role="region"
            aria-label="Outlook events to reconcile"
            tabIndex={0}
            sx={{ overflowX: 'auto', minWidth: 0, '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } }}
          >
            <Table sx={{ minWidth: 960, tableLayout: 'fixed' }} aria-busy={loading || undefined}>
              <colgroup>
                <col />
                <col />
                <col style={{ width: COLUMN_WIDTHS.when }} />
                <col />
                <col style={{ width: COLUMN_WIDTHS.type }} />
                <col style={{ width: COLUMN_WIDTHS.actions }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Calendar event</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Suggested type</TableCell>
                  <TableCell align="right" sx={stickyEnd(true)}>
                    <span className="sbg-sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderBody()}</TableBody>
            </Table>
          </Box>
        </Box>
      </SectionCard>

      {/* Row overflow menu */}
      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenu &&
          TIME_OFF_TYPE_ORDER.filter((type) => type !== rowMenu.entry.type).map((type) => (
            <MenuItem
              key={type}
              onClick={() => {
                const { entry } = rowMenu;
                closeRowMenu();
                handleReconcile(entry, { type });
              }}
            >
              <ListItemIcon>
                <Check size={16} />
              </ListItemIcon>
              <ListItemText>Confirm as {timeOffTypeLabel(type)}</ListItemText>
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => {
            if (!rowMenu) return;
            const { entry } = rowMenu;
            closeRowMenu();
            handleReanalyze(entry._id);
          }}
        >
          <ListItemIcon>
            <RotateCw size={16} />
          </ListItemIcon>
          <ListItemText>Re-run AI review</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!rowMenu) return;
            const { entry } = rowMenu;
            closeRowMenu();
            setDiscardDialog({ open: true, entry });
          }}
          sx={{ color: sbg.status.danger.text }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Trash2 size={16} />
          </ListItemIcon>
          <ListItemText>Discard event</ListItemText>
        </MenuItem>
      </Menu>

      {/* Discard confirmation */}
      <ConfirmDialog
        open={discardDialog.open}
        onClose={closeDiscardDialog}
        onConfirm={async () => {
          if (!discardDialog.entry) return;
          await handleDiscard(discardDialog.entry);
          closeDiscardDialog();
        }}
        destructive
        loading={loading}
        loadingLabel="Discarding…"
        title={discardDialog.entry ? `Discard ${discardDialog.entry.employee.name}’s event?` : 'Discard event?'}
        body={
          discardDialog.entry
            ? `${discardDialog.entry.originalCalendarSubject ? `“${discardDialog.entry.originalCalendarSubject}”, ` : ''}${formatDateRange(
                discardDialog.entry.startDate,
                discardDialog.entry.endDate,
              )}, is marked rejected and leaves this list. It won’t count as time off.`
            : undefined
        }
        confirmLabel="Discard event"
      />

      {/* Bulk review confirmation (copy is a snapshot of what was on screen when it opened) */}
      <ConfirmDialog
        open={bulkDialog.open}
        onClose={closeBulkDialog}
        onConfirm={async () => {
          await handleBulkReconcile(bulkDialog.entries);
          closeBulkDialog();
        }}
        loading={loading}
        loadingLabel="Marking reviewed…"
        title={`Mark ${formatCount(bulkDialog.entries.length, 'event')} reviewed?`}
        body={`Each event keeps its current type and category and leaves this list.${
          bulkDialog.hidden > 0 ? ` The ${formatCount(bulkDialog.hidden, 'event')} hidden by filters stay here.` : ''
        }`}
        confirmLabel={`Mark ${formatCount(bulkDialog.entries.length, 'event')} reviewed`}
      />

      {/* Edit sheet */}
      <Sheet
        open={!!editingEntry}
        onClose={handleCloseDialog}
        title="Edit Outlook event"
        subtitle={editingEntry ? `${editingEntry.employee.name} · ${formatDateRange(editingEntry.startDate, editingEntry.endDate)}` : undefined}
        actions={
          <>
            <Button variant="text" onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Check size={16} />}
              onClick={handleSaveEdit}
              disabled={loading}
              aria-busy={loading || undefined}
            >
              {loading ? 'Saving…' : 'Save and mark reviewed'}
            </Button>
          </>
        }
      >
        {editingEntry && (
          <>
            {sheetError && (
              <Alert severity="error" onClose={() => setSheetError(null)}>
                <AlertTitle>{sheetError.title}</AlertTitle>
                {failureAdvice(sheetError.failure, 'this event')} Your changes are still here.
                <ErrorDetails detail={sheetError.failure?.detail} />
              </Alert>
            )}

            <DefinitionList
              items={[
                { label: 'Employee', value: editingEntry.employee.name },
                { label: 'Email', value: editingEntry.employee.email || MISSING },
                { label: 'Department', value: editingEntry.employee.department?.name || MISSING },
                { label: 'Calendar subject', value: editingEntry.originalCalendarSubject || MISSING },
                {
                  label: 'Series',
                  value: editingEntry.isRecurring ? <StatusPill tone="neutral">Recurring series</StatusPill> : 'Single event',
                },
              ]}
            />

            <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary, mt: 1 }}>
              Time-off details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                value={toDateInputValue(editingEntry.startDate)}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  startDate: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End date"
                type="date"
                fullWidth
                value={toDateInputValue(editingEntry.endDate)}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  endDate: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Type"
                fullWidth
                value={editingEntry.type}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  type: e.target.value as TimeOffType
                })}
              >
                {TIME_OFF_TYPE_ORDER.map((type) => (
                  <MenuItem key={type} value={type}>
                    {TIME_OFF_TYPE_OPTION_LABEL[type]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Category"
                fullWidth
                value={editingEntry.category || ''}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  category: e.target.value as TimeOffCategory
                })}
              >
                {CATEGORY_ORDER.map((category) => (
                  <MenuItem key={category} value={category}>
                    {CATEGORY_OPTION_LABEL[category]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Hours"
                type="number"
                fullWidth
                value={editingEntry.hours}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  hours: Number(e.target.value)
                })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, step: 0.5, inputMode: 'decimal', style: { fontVariantNumeric: 'tabular-nums' } }}
                helperText="Use 0 for under 4 hours. It isn’t counted."
              />
              <TextField
                select
                label="Status"
                fullWidth
                value={editingEntry.status}
                onChange={(e) => setEditingEntry({
                  ...editingEntry,
                  status: e.target.value as TimeOffStatus
                })}
              >
                <MenuItem value={TimeOffStatus.PENDING}>Pending approval</MenuItem>
                <MenuItem value={TimeOffStatus.APPROVED}>Approved</MenuItem>
                <MenuItem value={TimeOffStatus.REJECTED}>Rejected</MenuItem>
              </TextField>
            </Box>
            <TextField
              label="Reason"
              fullWidth
              multiline
              minRows={2}
              value={editingEntry.reason || ''}
              onChange={(e) => setEditingEntry({
                ...editingEntry,
                reason: e.target.value
              })}
              placeholder="e.g. Family wedding in Chicago"
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={3}
              value={editingEntry.notes || ''}
              onChange={(e) => setEditingEntry({
                ...editingEntry,
                notes: e.target.value
              })}
            />
          </>
        )}
      </Sheet>

      {/* Success toast */}
      <Snackbar open={!!successMessage} autoHideDuration={5000} onClose={() => setSuccessMessage(null)}>
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OOOReconciler;
